import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import HeroAnimation from '../components/HeroAnimation';


import TechStack from '../components/TechStack';
import skillsData from '../data/skills.json';

const Home: React.FC = () => {
  


  // Extract first 6 skills from skillsData to showcase
  const technologies = skillsData
    .flatMap((category: any) => category.skills)
    .slice(0, 6)
    .map((skill) => ({ name: skill.name, icon: skill.icon, level: skill.level, description: skill.description }));

  const { t } = useTranslation('home');



  return (
    <div> {/* Hero Section */}
      <section
        className="relative flex min-h-[calc(100vh-64px)] items-center justify-center overflow-hidden"
        style={{ background: 'var(--app-page-gradient)' }}
      >
        {/* Enhanced Parallax Background */}
        <div className="absolute inset-0 z-0" style={{ background: 'var(--app-page-gradient)' }}>
          <HeroAnimation />
        </div>

        <div className="relative z-10 container mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.2 }}
            className="space-y-12 mx-auto"
          >
            {/* Enhanced Main Title */}
            <div className="space-y-6 text-center">
              <motion.div
                className="relative block mx-auto w-fit"
                initial={{ scale: 0.5, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 1, delay: 0.5 }}
              >
                <h1 className="text-7xl md:text-9xl font-display font-bold gradient-text text-shadow-lg relative z-10">
                  AlphaLuk
                </h1>
                <div className="absolute -inset-4 bg-gradient-to-r from-primary-500/20 to-secondary-500/20 blur-2xl rounded-full" />
              </motion.div>
              
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.8 }}
                className="space-y-4 mt-16 mx-auto w-fit"
              >
                <p className="text-2xl md:text-3xl text-dark-300 font-medium">{t('heroSubtitle')}</p>
              </motion.div>
            </div>

            {/* Enhanced CTA Buttons */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 1.2 }}
              className="flex flex-col sm:flex-row gap-5 justify-center items-center"
            >
              <Link to="/projects">
                <motion.button
                  whileHover={{ scale: 1.05, y: -2 }}
                  whileTap={{ scale: 0.95 }}
                  className="group relative px-10 py-4 bg-gradient-to-r from-primary-600 to-secondary-600 rounded-xl font-semibold text-purple-200 shadow-lg shadow-primary-500/30 transition-all duration-300 overflow-hidden"
                >
                  <span className="flex items-center space-x-3 text-lg">
                    <span>{t('projectsButton')}</span>
                    <motion.span
                      animate={{ x: [0, 5, 0] }}
                      transition={{ duration: 1.5, repeat: Infinity }}
                      className="text-xl"
                    >
                      →
                    </motion.span>
                  </span>
                </motion.button>
              </Link>
              
              <Link to="/contact">
                <motion.button
                  whileHover={{ scale: 1.05, y: -2 }}
                  whileTap={{ scale: 0.95 }}
                  className="group px-10 py-4 border border-primary-500/50 rounded-xl font-semibold text-primary-400 hover:text-primary-300 hover:bg-primary-500/10 hover:border-primary-500 transition-all duration-300"
                >
                  <span className="flex items-center space-x-3 text-lg">
                    <span>{t('contactButton')}</span>
                    <motion.span
                      animate={{ rotate: [0, 20, 0] }}
                      transition={{ duration: 1.5, repeat: Infinity }}
                      className="text-xl"
                    >
                      🤝
                    </motion.span>
                  </span>
                </motion.button>
              </Link>
            </motion.div>
          </motion.div>
        </div>

        {/* Enhanced Scroll Indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 2 }}
          className="absolute bottom-12 left-1/2 transform -translate-x-1/2"
        >
          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
            className="w-8 h-14 border-2 border-primary-400 rounded-full flex justify-center backdrop-blur-sm"
          >
            <motion.div
              animate={{ y: [0, 20, 0] }}
              transition={{ duration: 2, repeat: Infinity }}
              className="w-2 h-4 bg-gradient-to-b from-primary-400 to-secondary-400 rounded-full mt-2"
            />
          </motion.div>
        </motion.div>
      </section>

      <section className="py-16 relative overflow-hidden">
        <div className="container mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="max-w-3xl mx-auto"
          >
            <h2 className="text-4xl md:text-5xl font-display font-bold gradient-text mb-6">{t('upcomingTitle')}</h2>
            <p className="text-xl text-dark-300 mb-8 leading-relaxed">{t('upcomingDescription')}</p>
            <Link to="/coming-soon">
              <motion.button
                whileHover={{ scale: 1.05, y: -2 }}
                whileTap={{ scale: 0.95 }}
                className="group relative px-10 py-4 bg-gradient-to-r from-primary-600 to-secondary-600 rounded-xl font-semibold text-purple-200 shadow-lg shadow-primary-500/30 transition-all duration-300 overflow-hidden"
              >
                <span className="flex items-center space-x-3 text-lg">
                  <span>{t('upcomingButton')}</span>
                  <motion.span
                    animate={{ x: [0, 5, 0] }}
                    transition={{ duration: 1.5, repeat: Infinity }}
                    className="text-xl"
                  >
                    →
                  </motion.span>
                </span>
              </motion.button>
            </Link>
          </motion.div>
        </div>
      </section>




      {/* Tech Stack Preview */}
      <section className="py-16 relative overflow-hidden">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl md:text-5xl font-display font-bold gradient-text mb-4">{t('techTitle')}</h2>
            <p className="text-xl text-dark-300 max-w-2xl mx-auto">{t('techDescription')}</p>
            <div className="w-20 h-0.5 bg-gradient-to-r from-primary-500 to-secondary-500 rounded-full mx-auto mt-4" />
          </motion.div>
          
          <TechStack technologies={technologies} />

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.5 }}
            viewport={{ once: true }}
            className="text-center mt-10"
          >
            <Link to="/skills">
              <motion.button
                whileHover={{ scale: 1.05, y: -2 }}
                whileTap={{ scale: 0.95 }}
                className="group relative px-10 py-4 bg-gradient-to-r from-primary-600 to-secondary-600 rounded-xl font-semibold text-purple-200 shadow-lg shadow-primary-500/30 transition-all duration-300 overflow-hidden"
              >
                <span className="flex items-center space-x-3 text-lg">
                  <span>{t('skillsButton')}</span>
                  <motion.span
                    animate={{ x: [0, 5, 0] }}
                    transition={{ duration: 1.5, repeat: Infinity }}
                    className="text-xl"
                  >
                    →
                  </motion.span>
                </span>
              </motion.button>
            </Link>
          </motion.div>
        </div>
      </section>


      {/* CTA Section */}
      <section className="py-20 hidden relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_30%,rgba(168,85,247,0.1),transparent_70%)] animate-pulse" />
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-secondary-500/5 rounded-full mix-blend-multiply filter blur-3xl animate-blob animation-delay-2000" />
        </div>
        <div className="container mx-auto px-6 text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="max-w-3xl mx-auto"
          >
            <h2 className="text-4xl md:text-5xl font-display font-bold gradient-text mb-6">{t('ctaTitle')}</h2>
            <p className="text-xl text-dark-300 mb-8 leading-relaxed">{t('ctaDescription')}</p>
            <Link to="/contact">
              <motion.button
                whileHover={{ scale: 1.02, boxShadow: "0 10px 20px rgba(168, 85, 247, 0.1)" }}
                whileTap={{ scale: 0.95 }}
                className="group relative px-12 py-5 bg-gradient-to-r from-secondary-600 to-primary-600 rounded-2xl font-semibold text-purple-200 text-lg shadow-2xl hover:shadow-secondary-500/25 transition-all duration-100 overflow-hidden"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-secondary-400 to-primary-400 opacity-0 group-hover:opacity-20 transition-opacity duration-100" />
                <span className="flex items-center space-x-3">
                  <span>{t('ctaButton')}</span>
                  <motion.span
                    animate={{ rotate: [0, 20, 0] }}
                    transition={{ duration: 2, repeat: Infinity }}
                    className="text-2xl"
                  >
                    ✨
                  </motion.span>
                </span>
              </motion.button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Home;
